import { popoverMotion } from "../../../design-system/tokens/popoverMotion";
import styled from "styled-components";
import { borderRadius, spacing, typographyMixin } from "@magicbi/ui-library";

/** Prototype `.n2-hcard`: 384px wide, fixed, above the page. */
export const CARD_WIDTH = "24rem";
export const CARD_WIDTH_WIDE = "min(44rem, calc(100vw - 1rem))";

export const Card = styled.div<{ $wide: boolean }>`
  position: fixed;
  ${popoverMotion()}
  z-index: 1300;
  display: grid;
  gap: ${spacing[2]};
  width: ${({ $wide }) => ($wide ? CARD_WIDTH_WIDE : CARD_WIDTH)};
  max-width: calc(100vw - 1rem);
  max-height: calc(100vh - 1rem);
  box-sizing: border-box;
  overflow: ${({ $wide }) => ($wide ? "hidden" : "auto")};
  grid-template-rows: ${({ $wide }) => ($wide ? "auto minmax(0, 1fr)" : "none")};
  padding: ${spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.border.stroke};
  border-radius: ${borderRadius.xl};
  background: ${({ theme }) => theme.colors.surface.primary};
  box-shadow: ${({ theme }) => theme.shadows.component.dropdown};
`;

/** Wide layout body: the list scrolls on its own, the preview column stays put. */
export const Body = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: ${spacing[3]};
  min-height: 0;

  @media (max-width: 40rem) {
    grid-template-columns: minmax(0, 1fr);
    overflow: auto;
  }
`;

export const Aside = styled.div`
  display: grid;
  align-content: start;
  gap: ${spacing[2]};
  min-width: 0;

  @media (max-width: 40rem) {
    order: -1;
  }
`;

export const ListColumn = styled.div`
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  min-height: 0;
  min-width: 0;

  @media (max-width: 40rem) {
    min-height: 12rem;
  }
`;

export const Facts = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[1]};
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    padding: 0 ${spacing[1]};
    border-radius: ${borderRadius.full};
    background: ${({ theme }) => theme.colors.surface.secondary};
    ${typographyMixin("caption", 4)}
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

export const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[2]};
`;

export const HeadText = styled.div`
  display: grid;
  min-width: 0;
`;

export const Title = styled.b<{ $error: boolean }>`
  ${typographyMixin("body", 2)}
  color: ${({ theme, $error }) => ($error ? theme.colors.error.text : theme.colors.text.primary)};
`;

export const Subtitle = styled.span`
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Tag = styled.span`
  flex: none;
  padding: 0 ${spacing[1]};
  border-radius: ${borderRadius.full};
  background: ${({ theme }) => theme.colors.surface.secondary};
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const Note = styled.p`
  display: flex;
  align-items: flex-start;
  gap: ${spacing[2]};
  margin: 0;
  padding: ${spacing[2]};
  border-radius: ${borderRadius.lg};
  background: ${({ theme }) => theme.colors.background.tertiary};
  ${typographyMixin("caption", 4)}
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    flex: none;
    width: 0.75rem;
    height: 0.75rem;
    margin-top: ${spacing[0.5]};
    color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[2]};
`;

export const ListLabel = styled.span`
  padding: ${spacing[1]} ${spacing[1]} 0;
  ${typographyMixin("caption", 2)}
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const List = styled.div<{ $scroll?: boolean }>`
  display: grid;
  align-content: start;
  gap: ${spacing[0.5]};
  ${({ $scroll }) => ($scroll ? "min-height: 0; overflow-y: auto; overscroll-behavior: contain;" : "")}
`;

export const Option = styled.button<{ $media?: boolean }>`
  display: grid;
  grid-template-columns: ${({ $media }) => ($media ? "6rem minmax(0, 1fr) 1.125rem" : "minmax(0, 1fr) 1.125rem")};
  align-items: center;
  gap: ${spacing[2]};
  width: 100%;
  padding: ${spacing[1]} ${spacing[2]};
  border: 0;
  border-radius: ${borderRadius.lg};
  background: none;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: left;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    outline: none;
    background: ${({ theme }) => theme.colors.surface.secondary};
  }

  &:focus-visible {
    box-shadow: inset 0 0 0 2px ${({ theme }) => theme.colors.primary[500]};
  }

  &[aria-selected="true"] {
    background: ${({ theme }) => theme.colors.background.tertiary};
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: ${({ theme }) => theme.colors.primary[500]};
  }
`;

export const OptionText = styled.span`
  display: grid;
  min-width: 0;

  b {
    ${typographyMixin("body", 2)}
  }

  span {
    ${typographyMixin("caption", 4)}
    color: ${({ theme }) => theme.colors.text.secondary};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const OptionMedia = styled.span`
  display: block;
  min-width: 0;
`;
